import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { WORD_TYPES, wordTypeLabel, type WordType } from "../../domain/vocabulary/types.ts";
import { filterGroupsByTypes, groupWords, searchGroups } from "../../domain/vocabulary/grouping.ts";
import { isMeaningPracticeReady } from "../../domain/exercises/validate.ts";
import { cardIneligibleReason } from "../../domain/practice/cards.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { usePractice, useVocabularySnapshot } from "../hooks/bindings.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function PracticeSelectScreen() {
  const snapshot = useVocabularySnapshot();
  const { practice } = useAppServices();
  const state = usePractice();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [types, setTypes] = useState<WordType[]>([]);
  const selected = new Set(state.setup.selectedMeaningIds);
  const groups = useMemo(
    () => filterGroupsByTypes(searchGroups(groupWords(snapshot), query), types),
    [snapshot, query, types],
  );

  function toggleMeaning(id: string) {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    practice.setSelectedMeanings([...next]);
  }

  function toggleGroup(ids: string[]) {
    const allOn = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    for (const id of ids) {
      if (allOn) {
        next.delete(id);
      } else {
        next.add(id);
      }
    }
    practice.setSelectedMeanings([...next]);
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title="Select Words" backTo="/practice/setup" />
        <p className={styles.sub}>Choose specific meanings to practice.</p>
        <input
          className={styles.input}
          placeholder="Search words…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className={styles.chips}>
          {WORD_TYPES.map((type) => (
            <button
              key={type}
              className={`${styles.chip} ${types.includes(type) ? styles.chipActive : ""}`}
              type="button"
              aria-pressed={types.includes(type)}
              onClick={() =>
                setTypes((current) =>
                  current.includes(type) ? current.filter((item) => item !== type) : [...current, type],
                )
              }
            >
              {wordTypeLabel(type)}
            </button>
          ))}
        </div>
        <div className={styles.sectionHeading}>
          <span>
            {groups.length} {groups.length === 1 ? "word" : "words"}
          </span>
          <button className={styles.textButton} type="button" onClick={() => practice.setSelectedMeanings([])}>
            Clear all
          </button>
        </div>
        {groups.map((group) => {
          const ready = group.meanings.filter((meaning) =>
            state.setup.format === "cards"
              ? cardIneligibleReason(snapshot, meaning, state.setup.direction) == null
              : isMeaningPracticeReady(snapshot, meaning),
          );
          const ids = ready.map((meaning) => meaning.id);
          const selectedCount = ids.filter((id) => selected.has(id)).length;
          return (
            <section key={group.word.id}>
              <label className={styles.groupCheck}>
                <input
                  type="checkbox"
                  checked={ids.length > 0 && selectedCount === ids.length}
                  disabled={ids.length === 0}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = selectedCount > 0 && selectedCount < ids.length;
                    }
                  }}
                  onChange={() => toggleGroup(ids)}
                />
                <span className={styles.groupCheckTitle}>{group.word.displayText}</span>
              </label>
              {ready.map((meaning) => (
                <label key={meaning.id} className={`${styles.sense} ${selected.has(meaning.id) ? styles.senseSelected : ""}`}>
                  <input
                    type="checkbox"
                    checked={selected.has(meaning.id)}
                    onChange={() => toggleMeaning(meaning.id)}
                  />
                  <div>
                    <TypeTag type={meaning.type} />
                    <h3>{meaning.shortLabel}</h3>
                  </div>
                </label>
              ))}
            </section>
          );
        })}
        {state.warning ? <p className={styles.error}>{state.warning}</p> : null}
        <div className={styles.footerAction}>
          <PrimaryButton
            onClick={() => {
              if (practice.start()) {
                navigate("/practice/session");
              }
            }}
          >
            Use selected meanings ({selected.size})
          </PrimaryButton>
        </div>
      </Screen>
    </Frame>
  );
}
